import api from './api';
import { areRuntimeMocksEnabled } from './runtimeMocks';

// Types et interfaces pour les réclamations
export interface Complaint {
  id: number;
  reference: string;
  customer_id: number;
  order_id?: number;
  product_id?: number;
  intervention_id?: number;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'rejected' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  resolution?: string;
  created_at: string;
  updated_at: string;
  customer: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  order?: {
    id: number;
    order_number: string;
  };
  product?: {
    id: number;
    name: string;
    sku: string;
  };
  intervention?: {
    id: number;
    title: string;
  };
  notes: Array<{
    id: number;
    content: string;
    created_by: number;
    created_at: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
    };
  }>;
}

export interface ComplaintFilters {
  status?: string;
  priority?: string;
  customer_id?: number;
  order_id?: number;
  product_id?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ComplaintsResponse {
  success: boolean;
  data: {
    complaints: Complaint[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Données mockées pour les réclamations
const mockComplaints: Complaint[] = [
  {
    id: 1,
    reference: 'REC-2024-001',
    customer_id: 1,
    order_id: 1,
    product_id: 1,
    subject: 'Problème de chaudière',
    description: 'La chaudière ne se met pas en marche depuis 2 jours. Nous avons vérifié l\'alimentation électrique et tout semble normal.',
    status: 'in_progress',
    priority: 'high',
    resolution: undefined,
    created_at: '2024-01-15T14:30:00Z',
    updated_at: '2024-01-16T10:15:00Z',
    customer: {
      id: 1,
      first_name: 'Jean',
      last_name: 'Dupont',
      email: 'jean.dupont@email.com'
    },
    order: {
      id: 1,
      order_number: 'CMD-2024-001'
    },
    product: {
      id: 1,
      name: 'Chaudière Viessmann Vitopend 100-W',
      sku: 'VIE-100W-001'
    },
    notes: [
      {
        id: 1,
        content: 'Technicien contacté, intervention prévue demain matin',
        created_by: 1,
        created_at: '2024-01-16T10:15:00Z',
        user: {
          id: 1,
          first_name: 'Marie',
          last_name: 'Support'
        }
      }
    ]
  },
  {
    id: 2,
    reference: 'REC-2024-002',
    customer_id: 2,
    order_id: 2,
    subject: 'Retard de livraison',
    description: 'Ma pompe à chaleur devait être livrée le 25 janvier mais je n\'ai toujours rien reçu. Pouvez-vous me donner des nouvelles ?',
    status: 'open',
    priority: 'medium',
    resolution: undefined,
    created_at: '2024-01-26T16:45:00Z',
    updated_at: '2024-01-26T16:45:00Z',
    customer: {
      id: 2,
      first_name: 'Marie',
      last_name: 'Martin',
      email: 'marie.martin@email.com'
    },
    order: {
      id: 2,
      order_number: 'CMD-2024-002'
    },
    product: {
      id: 3,
      name: 'Pompe à chaleur Daikin Altherma',
      sku: 'DAI-ALT-001'
    },
    notes: []
  },
  {
    id: 3,
    reference: 'REC-2024-003',
    customer_id: 3,
    intervention_id: 1,
    subject: 'Insatisfaction intervention',
    description: 'Le technicien est passé mais n\'a pas résolu le problème. Le radiateur fait toujours du bruit.',
    status: 'resolved',
    priority: 'low',
    resolution: 'Nouveau passage technicien effectué. Problème résolu avec remplacement du thermostat défaillant.',
    created_at: '2024-01-18T11:20:00Z',
    updated_at: '2024-01-22T14:30:00Z',
    customer: {
      id: 3,
      first_name: 'Pierre',
      last_name: 'Dubois',
      email: 'pierre.dubois@email.com'
    },
    intervention: {
      id: 1,
      title: 'Maintenance radiateur électrique'
    },
    notes: [
      {
        id: 2,
        content: 'Planification d\'une nouvelle intervention avec un technicien senior',
        created_by: 1,
        created_at: '2024-01-19T09:00:00Z',
        user: {
          id: 1,
          first_name: 'Marie',
          last_name: 'Support'
        }
      },
      {
        id: 3,
        content: 'Intervention effectuée, thermostat remplacé, client satisfait',
        created_by: 2,
        created_at: '2024-01-22T14:30:00Z',
        user: {
          id: 2,
          first_name: 'Paul',
          last_name: 'Technicien'
        }
      }
    ]
  },
  {
    id: 4,
    reference: 'REC-2024-004',
    customer_id: 1,
    subject: 'Demande de remboursement',
    description: 'Suite au problème récurrent sur ma chaudière, je souhaiterais être remboursé.',
    status: 'on_hold',
    priority: 'critical',
    resolution: undefined,
    created_at: '2024-01-20T13:15:00Z',
    updated_at: '2024-01-21T08:45:00Z',
    customer: {
      id: 1,
      first_name: 'Jean',
      last_name: 'Dupont',
      email: 'jean.dupont@email.com'
    },
    notes: [
      {
        id: 4,
        content: 'Dossier transmis au service juridique pour évaluation',
        created_by: 3,
        created_at: '2024-01-21T08:45:00Z',
        user: {
          id: 3,
          first_name: 'Sophie',
          last_name: 'Manager'
        }
      }
    ]
  }
];

// Fonction pour simuler un délai réseau
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class ComplaintsService {
  // Récupérer la liste des réclamations
  async getComplaints(filters: ComplaintFilters = {}): Promise<ComplaintsResponse> {
    try {
      // Essayer d'utiliser l'API réelle en premier
  const response = await api.get('/complaints', { params: filters });
      console.log('Récupération des réclamations depuis l\'API');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des réclamations via API:', error);

      if (areRuntimeMocksEnabled()) {
        await delay(500);
        let complaints = [...mockComplaints];

        if (filters.status) complaints = complaints.filter(c => c.status === filters.status);
        if (filters.priority) complaints = complaints.filter(c => c.priority === filters.priority);
        if (filters.customer_id) complaints = complaints.filter(c => c.customer_id === filters.customer_id);
        if (filters.search) {
          const search = filters.search.toLowerCase();
          complaints = complaints.filter(c =>
            c.reference.toLowerCase().includes(search) ||
            c.subject.toLowerCase().includes(search) ||
            c.description.toLowerCase().includes(search)
          );
        }

        const page = filters.page || 1;
        const limit = filters.limit || 10;
        const paginatedComplaints = complaints.slice((page - 1) * limit, page * limit);

        console.warn('API indisponible : mode mock explicite activé pour la lecture des réclamations');
        return {
          success: true,
          data: {
            complaints: paginatedComplaints,
            total: complaints.length,
            page,
            limit,
            totalPages: Math.ceil(complaints.length / limit)
          }
        };
      }
      
      throw error;
    }
  }

  // Récupérer une réclamation par son ID
  async getComplaintById(id: number): Promise<Complaint> {
    try {
      // Utiliser l'API réelle pour récupérer la réclamation
      const response = await api.get(`/complaints/${id}`);
      
      if (!response.data.success) {
        throw new Error(`Réclamation avec l'ID ${id} non trouvée`);
      }
      
      console.log(`Récupération de la réclamation ${id} depuis l'API`);
      
      // Adapter les données de l'API pour correspondre au format frontend
      const complaint = response.data.data;
      return {
        id: complaint.id,
        reference: complaint.reference,
        customer_id: complaint.customerId,
        order_id: complaint.orderId,
        product_id: complaint.productId,
        intervention_id: complaint.interventionId,
        subject: complaint.subject,
        description: complaint.description,
        status: complaint.status,
        priority: complaint.priority,
        resolution: complaint.resolution,
        created_at: complaint.createdAt,
        updated_at: complaint.updatedAt,
        customer: complaint.customer ? {
          id: complaint.customer.id,
          first_name: complaint.customer.first_name,
          last_name: complaint.customer.last_name,
          email: complaint.customer.user?.email || ''
        } : {
          id: 0,
          first_name: '',
          last_name: '',
          email: ''
        },
        order: complaint.order ? {
          id: complaint.order.id,
          order_number: complaint.order.reference || `ORDER-${complaint.order.id}`
        } : undefined,
        product: complaint.product ? {
          id: complaint.product.id,
          name: complaint.product.nom,
          sku: complaint.product.reference
        } : undefined,
        notes: (complaint.notes || []).map((n: any) => ({
          id: n.id,
          content: n.note,
          created_by: n.userId,
          created_at: n.created_at || n.createdAt,
          user: n.user ? {
            id: n.user.id,
            first_name: n.user.first_name,
            last_name: n.user.last_name
          } : { id: 0, first_name: 'Utilisateur', last_name: 'Support' }
        })).sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de la réclamation ${id}:`, error);
      throw error;
    }
  }

  // Créer une nouvelle réclamation (API réelle)
  async createComplaint(complaintData: Partial<Complaint>): Promise<Complaint> {
    try {
      // Adapter les champs pour l'API backend
      const payload = {
        customerId: complaintData.customer_id,
        orderId: complaintData.order_id,
        productId: complaintData.product_id,
        interventionId: complaintData.intervention_id,
        subject: complaintData.subject,
        description: complaintData.description,
        priority: complaintData.priority,
        // Ajouter d'autres champs si besoin
      };
      const response = await api.post('/complaints', payload);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la création de la réclamation (API):', error);
      throw error;
    }
  }

  // Mettre à jour une réclamation
  async updateComplaint(id: number, complaintData: Partial<Complaint>): Promise<Complaint> {
    try {
      // Si on change uniquement le statut (et éventuellement la résolution), 
      // utiliser la route PATCH /complaints/:id/status qui déclenche les notifications push FCM
      const changedKeys = Object.keys(complaintData).filter(k => complaintData[k as keyof Complaint] !== undefined);
      const statusOnly = complaintData.status !== undefined && 
                        changedKeys.every(k => k === 'status' || k === 'resolution');
      
      if (statusOnly) {
        console.log('Changement de statut uniquement -> utilisation de PATCH /status (notifications FCM)');
        const statusPayload: any = {
          status: complaintData.status
        };
        if (complaintData.resolution !== undefined) {
          statusPayload.resolution = complaintData.resolution;
        }
        const response = await api.patch(`/complaints/${id}/status`, statusPayload);
        
        if (!response.data.success) {
          throw new Error(`Erreur lors de la mise à jour du statut de la réclamation ${id}`);
        }
        
        console.log(`✅ Statut de la réclamation ${id} mis à jour (notification envoyée au client)`);
        
        // Recharger la réclamation complète avec les relations
        const fullComplaint = await this.getComplaintById(id);
        return fullComplaint;
      }
      
      // Sinon, utiliser PUT pour les autres modifications
      const payload: any = {};
      
      // Champs modifiables lors d'une mise à jour
      if (complaintData.subject !== undefined) payload.subject = complaintData.subject;
      if (complaintData.description !== undefined) payload.description = complaintData.description;
      if (complaintData.priority !== undefined) payload.priority = complaintData.priority;
      if (complaintData.status !== undefined) payload.status = complaintData.status;
      if (complaintData.resolution !== undefined) payload.resolution = complaintData.resolution;
      
      // Ne pas envoyer les IDs de relations lors d'une mise à jour simple
      // customerId, orderId, productId sont généralement fixés à la création

      console.log('Payload envoyé pour mise à jour:', payload);

      // Utiliser l'API réelle pour mettre à jour la réclamation
      const response = await api.put(`/complaints/${id}`, payload);
      
      if (!response.data.success) {
        throw new Error(`Erreur lors de la mise à jour de la réclamation ${id}`);
      }
      
      console.log(`Mise à jour de la réclamation ${id} depuis l'API`);
      
      // Adapter les données de l'API pour correspondre au format frontend
      const complaint = response.data.data;
      return {
        id: complaint.id,
        reference: complaint.reference,
        customer_id: complaint.customerId,
        order_id: complaint.orderId,
        product_id: complaint.productId,
        intervention_id: complaint.interventionId,
        subject: complaint.subject,
        description: complaint.description,
        status: complaint.status,
        priority: complaint.priority,
        resolution: complaint.resolution,
        created_at: complaint.createdAt,
        updated_at: complaint.updatedAt,
        customer: complaint.customer ? {
          id: complaint.customer.id,
          first_name: complaint.customer.first_name,
          last_name: complaint.customer.last_name,
          email: complaint.customer.user?.email || ''
        } : {
          id: 0,
          first_name: '',
          last_name: '',
          email: ''
        },
        order: complaint.order ? {
          id: complaint.order.id,
          order_number: complaint.order.reference || `ORDER-${complaint.order.id}`
        } : undefined,
        product: complaint.product ? {
          id: complaint.product.id,
          name: complaint.product.nom,
          sku: complaint.product.reference
        } : undefined,
        notes: (complaint.notes || []).map((n: any) => ({
          id: n.id,
          content: n.note,
          created_by: n.userId,
          created_at: n.created_at || n.createdAt,
          user: n.user ? {
            id: n.user.id,
            first_name: n.user.first_name,
            last_name: n.user.last_name
          } : { id: 0, first_name: 'Utilisateur', last_name: 'Support' }
        })).sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      };
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la réclamation ${id}:`, error);
      throw error;
    }
  }

  // Supprimer une réclamation
  async deleteComplaint(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/complaints/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la réclamation ${id}:`, error);
      throw error;
    }
  }

  // Mettre à jour le statut d'une réclamation
  async updateStatus(id: number, status: string): Promise<Complaint> {
    try {
      const response = await api.patch(`/complaints/${id}/status`, { status });
      if (!response.data.success) {
        throw new Error(response.data.message || `Erreur lors de la mise à jour du statut de la réclamation ${id}`);
      }
      return response.data.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut de la réclamation ${id}:`, error);
      throw error;
    }
  }

  // Ajouter une note à une réclamation
  async addNote(complaintId: number, content: string): Promise<Complaint['notes'][0]> {
    try {
      const response = await api.post(`/complaints/${complaintId}/notes`, { note: content });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de l\'ajout de la note');
      }
      
      const n = response.data.data;
      return {
        id: n.id,
        content: n.note,
        created_by: n.userId,
        created_at: n.created_at || n.createdAt,
        user: n.user ? {
          id: n.user.id,
          first_name: n.user.first_name,
          last_name: n.user.last_name
        } : { id: 0, first_name: 'Utilisateur', last_name: 'Inconnu' }
      };
    } catch (error) {
      console.error(`Erreur lors de l'ajout d'une note à la réclamation ${complaintId}:`, error);
      throw error;
    }
  }

  // Mettre à jour une note de réclamation
  async updateNote(complaintId: number, noteId: number, content: string): Promise<Complaint['notes'][0]> {
    try {
      const response = await api.put(`/complaints/${complaintId}/notes/${noteId}`, { note: content });
      if (!response.data.success) {
        throw new Error(response.data.message || `Erreur lors de la mise à jour de la note ${noteId}`);
      }
      const note = response.data.data;
      return {
        id: note.id,
        content: note.note,
        created_by: note.userId,
        created_at: note.created_at || note.createdAt,
        user: note.user ? {
          id: note.user.id,
          first_name: note.user.first_name,
          last_name: note.user.last_name
        } : { id: 0, first_name: 'Utilisateur', last_name: 'Inconnu' }
      };
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la note ${noteId}:`, error);
      throw error;
    }
  }

  // Supprimer une note de réclamation
  async deleteNote(complaintId: number, noteId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/complaints/${complaintId}/notes/${noteId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la note ${noteId}:`, error);
      throw error;
    }
  }
}

export const complaintsService = new ComplaintsService();
